import { useLanguage } from "@/i18n/LanguageProvider";
import { useQuotaMeter } from "@/hooks/useQuotaMeter";
import { useCredits } from "@/hooks/useCredits";
import { unitsToCredits } from "@/config/credits";

// V4.3 quota meter — text (X/day · Y/month, Free only) + voice TTS minutes
// (all tiers). Display-only; the backend enforces the caps.
//
// O número de texto são UNIDADES PONDERADAS (06/08), não contagem de
// chamadas: conversa com o Espelho custa 1,0, documento 2,0, agenda/tarefa
// 0,5, sleep/language 0,1. É o mesmo número que o gate compara — mostrar
// "12 mensagens" enquanto o servidor bloqueia em 12,5 unidades faria o
// medidor discordar do que trava, que é o pior defeito possível num medidor.

function Bar({ used, limit }: { used: number; limit: number }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const over = limit > 0 && used >= limit;
  return (
    <div className="h-1.5 w-full rounded-full bg-foreground/[0.08] overflow-hidden">
      <div
        className="h-full rounded-full transition-[width] duration-300"
        style={{
          width: `${pct}%`,
          background: over
            ? "hsl(0 70% 55%)"
            : "linear-gradient(90deg, hsl(187 72% 45%), hsl(187 72% 60%))",
        }}
      />
    </div>
  );
}

function Row({ label, value, used, limit }: { label: string; value: string; used: number; limit: number }) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-1.5">
        <p className="font-display text-[13px] font-medium text-foreground/85">{label}</p>
        <p className="font-body text-[12px] text-foreground/55 tabular-nums">{value}</p>
      </div>
      <Bar used={used} limit={limit} />
    </div>
  );
}

/**
 * Unidades ponderadas legíveis: inteiro sai sem casa decimal ("12"),
 * fracionário sai com uma ("12.5"). Sem isto o medidor mostraria "12.00 / 40"
 * no caso comum, que lê como erro de formatação.
 */
function fmtUnits(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export default function QuotaMeter() {
  const { t } = useLanguage();
  const m = useQuotaMeter();
  const credits = useCredits();

  if (m.isLoading) return null;

  // Voice minutes formatting (one decimal, locale comma handled by toFixed→replace).
  const ttsUsedMin = (m.ttsUsedSeconds / 60).toFixed(1);
  const ttsLimitMin = Math.round(m.ttsLimitSeconds / 60);
  const minUnit = t("quota.meter.minUnit");
  // "via credits": a tier bucket is spent, so usage is now drawing on credits.
  const quotaSpent =
    m.ttsUsedSeconds >= m.ttsLimitSeconds ||
    (m.isFree && (m.textUsedMonth >= m.textMonthlyLimit || m.textUsedToday >= m.textDailyLimit));

  return (
    <div className="vyv-card overflow-hidden divide-y divide-foreground/[0.05]">
      {/* Mostra sempre que EXISTE teto, não só no Free (06/08). Desde que os
          satélites passaram a contar todos os tiers, um Premium também pode
          ser bloqueado — e esconder o medidor dele reproduz, para quem paga, o
          mesmo defeito que a mudança de unidades veio corrigir: o usuário não
          vê nada e de repente trava. Teto ausente (tier fora do mapa) segue
          sem linha, porque aí não há o que medir. */}
      {m.textMonthlyLimit > 0 && (
        <>
          <Row
            label={t("quota.meter.textDaily")}
            value={`${fmtUnits(m.textUsedToday)} / ${fmtUnits(m.textDailyLimit)}`}
            used={m.textUsedToday}
            limit={m.textDailyLimit}
          />
          <Row
            label={t("quota.meter.textMonthly")}
            value={`${fmtUnits(m.textUsedMonth)} / ${fmtUnits(m.textMonthlyLimit)}`}
            used={m.textUsedMonth}
            limit={m.textMonthlyLimit}
          />
        </>
      )}
      <Row
        label={t("quota.meter.voice")}
        value={`${ttsUsedMin} / ${ttsLimitMin} ${minUnit}`}
        used={m.ttsUsedSeconds}
        limit={m.ttsLimitSeconds}
      />
      {credits.balanceUnits > 0 && (
        <div className="px-4 py-3 flex items-center justify-between">
          <p className="font-display text-[13px] font-medium text-foreground/85">
            {t("quota.meter.credits")}
          </p>
          <div className="flex items-center gap-2">
            {quotaSpent && (
              <span
                className="text-[9px] font-display font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                style={{ background: "hsl(45 90% 55% / 0.15)", border: "1px solid hsl(45 90% 55% / 0.3)", color: "hsl(45 90% 65%)" }}
              >
                {t("quota.meter.viaCredits")}
              </span>
            )}
            <p className="font-body text-[12px] text-foreground/55 tabular-nums">
              {unitsToCredits(credits.balanceUnits)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
